import { useCallback, useState } from 'react';
import type { Stripe } from '@stripe/stripe-js';
import {
    stripePromise,
    rememberPendingStripeReturn,
    clearPendingStripeReturn,
    type PendingStripeReturn,
} from '../utils/stripe';

/** Any server answer that may ask the browser to run a 3D Secure challenge. */
export interface StripeActionResponse {
    success?: boolean;
    outcome?: string;
    /** Signup's spelling of outcome === 'requires_action'. */
    requiresAction?: boolean;
    clientSecret?: string;
    message?: string;
}

export const BANK_CONFIRMATION_MESSAGE = 'Waiting for your bank to confirm…';

/** A second requires_action after finalize gets one more challenge; beyond that we stop. */
const MAX_CHALLENGES = 2;

export function needsStripeAction(response: StripeActionResponse | null | undefined): boolean {
    return !!response
        && !!response.clientSecret
        && (response.outcome === 'requires_action' || response.requiresAction === true);
}

function failed<T extends StripeActionResponse>(from: T, message: string): T {
    return {
        ...from,
        success: false,
        outcome: 'failed',
        requiresAction: false,
        clientSecret: undefined,
        message,
    } as T;
}

export interface CompleteStripePaymentOptions {
    /** Saved to sessionStorage before the challenge so a redirect-based one can resume on return. */
    pending?: PendingStripeReturn;
    /** Called once, right before the first challenge starts. */
    onChallengeStart?: () => void;
    /** Test seam; defaults to the shared Stripe instance. */
    getStripe?: () => Promise<Stripe | null>;
}

/**
 * Passes a server response through unchanged unless it is requires_action. Then runs
 * stripe.handleNextAction and asks the server to finalize. A challenge error returns a failed
 * result without finalizing: no money moved, and the server's requires_action row is swept later.
 */
export async function completeStripePayment<T extends StripeActionResponse>(
    response: T,
    finalize: () => Promise<T>,
    options: CompleteStripePaymentOptions = {},
): Promise<T> {
    const getStripe = options.getStripe ?? (() => stripePromise);
    let current = response;
    let started = false;

    for (let challenge = 0; challenge < MAX_CHALLENGES; challenge++) {
        if (!needsStripeAction(current)) return current;

        if (!started) {
            started = true;
            options.onChallengeStart?.();
        }

        const stripe = await getStripe();
        if (!stripe) return failed(current, 'Could not load the payment processor. Please try again.');

        if (options.pending) rememberPendingStripeReturn(options.pending);
        let challengeError: string | null = null;
        try {
            const { error } = await stripe.handleNextAction({ clientSecret: current.clientSecret! });
            if (error) challengeError = error.message || 'Your bank did not confirm the payment.';
        } finally {
            // Reaching here means no redirect happened; the return page has nothing to resume.
            if (options.pending) clearPendingStripeReturn();
        }
        if (challengeError) return failed(current, challengeError);

        current = await finalize();
    }

    return needsStripeAction(current)
        ? failed(current, 'Your bank did not confirm the payment. Please try again.')
        : current;
}

/**
 * React wrapper around completeStripePayment that exposes `awaitingBank` for a
 * "Waiting for your bank to confirm…" state while the challenge and finalize run.
 */
export function useStripePaymentCompletion() {
    const [awaitingBank, setAwaitingBank] = useState(false);

    const completePayment = useCallback(async <T extends StripeActionResponse>(
        response: T,
        finalize: () => Promise<T>,
        pending?: PendingStripeReturn,
    ): Promise<T> => {
        try {
            return await completeStripePayment(response, finalize, {
                pending,
                onChallengeStart: () => setAwaitingBank(true),
            });
        } finally {
            setAwaitingBank(false);
        }
    }, []);

    return { completePayment, awaitingBank };
}
